import { SIDE } from '@allocab/allocab-commons-shared-types';
import { DocumentItemOutput } from '@allocab/allocab-driver-service-types';
import { useState } from 'react';

import { DocumentPreview } from './DocumentPreview';
import { useUploadDocument } from '../../hooks/useUploadDocument';
import { t } from '../../services/translation/translation';
import { BadgeIndicator } from '../BadgeIndicator';
import { PrimaryButton } from '../button/PrimaryButton';
import { SecondaryButton } from '../button/SecondaryButton';
import { DragAndDropInput } from '../DragAndDropInput';

type Props = {
  title: string;
  documentItem: DocumentItemOutput;
  side: SIDE;
  vehicleId?: string;
  driverKey?: string;
};

export const DocumentCard = ({ title, documentItem, side, vehicleId, driverKey }: Props) => {
  const [showFileInput, setShowFileInput] = useState(false);
  const status = (side === 'RECTO' ? documentItem.doc?.statusRecto : documentItem.doc?.statusVerso) ?? 'TO_DEFINE';
  const [uploadProgress, setUploadProgress] = useState(0);

  const { mutate: uploadDocument, isLoading: isUploading } = useUploadDocument();

  const handleFileUpload = (list: FileList | null) => {
    if (list) {
      const file = list[0];

      if (file) {
        uploadDocument(
          { file, side, type: documentItem.type, vehicleId, driverKey, onUploadProgress: setUploadProgress },
          { onSuccess: () => setShowFileInput(false), onSettled: () => setUploadProgress(0) },
        );
      }
    }
  };

  const hadDocUploaded =
    documentItem.doc && (side === 'RECTO' ? !!documentItem.doc.fileNameRecto : !!documentItem.doc.fileNameVerso);
  const showPreview = !showFileInput && hadDocUploaded;

  return (
    <div className="inline-flex flex-col flex-1 gap-4 p-4 bg-white border border-gray-200 rounded-lg shadow dark:bg-gray-800 dark:border-gray-700">
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <h6 className="text-lg font-bold dark:text-white">{title}</h6>
          {status !== 'TO_DEFINE' ? (
            <BadgeIndicator
              variant={
                status === 'VALIDATED'
                  ? 'green'
                  : status === 'MODIFIED'
                  ? 'yellow'
                  : status === 'REJECTED'
                  ? 'red'
                  : 'gray'
              }
            >
              {status === 'VALIDATED'
                ? t('common.validated')
                : status === 'MODIFIED'
                ? t('common.validating')
                : status === 'REJECTED'
                ? t('common.rejected')
                : null}
            </BadgeIndicator>
          ) : null}
        </div>
        {hadDocUploaded && !isUploading ? (
          showPreview ? (
            <PrimaryButton text={t('common.update')} onClick={() => setShowFileInput(true)} />
          ) : (
            <SecondaryButton text={t('common.cancel')} onClick={() => setShowFileInput(false)} />
          )
        ) : null}
      </div>
      <div className="relative w-full aspect-square">
        {isUploading ? (
          <div className="h-full flex flex-col justify-center">
            <div className="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
              <div className="bg-blue-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
            </div>
          </div>
        ) : showPreview && documentItem.doc ? (
          <DocumentPreview documentId={documentItem.doc.id} side={side} driverKey={driverKey} />
        ) : (
          <DragAndDropInput onChange={handleFileUpload} />
        )}
      </div>
    </div>
  );
};
